// Libraries
import { cloneDeep } from "lodash-es";
import { Grid, Typography } from "@mui/material";

// Types
import {
	IPayrollProviderCustomFieldOption,
	ISystemSettingsMappedCustomField,
} from "@/Settings/types/PayrollMapping/CustomFieldItemsMapping/api";
import { TSelectedValuesInput } from "@/Layouts/types/HierarchicalSelector";

// Components
import CustomFieldItemRow from "./Row";

type TMappingSectionProps = {
	disabled: boolean;
	customFieldsOptions: IPayrollProviderCustomFieldOption[];
	mappedCustomFields: ISystemSettingsMappedCustomField[];
	setMappedCustomFields: React.Dispatch<React.SetStateAction<ISystemSettingsMappedCustomField[]>>;
};

export default function MappingSection(props: Readonly<TMappingSectionProps>) {
	const { customFieldsOptions, mappedCustomFields, disabled, setMappedCustomFields } = props;

	function doAnyChange(actionFn: (mappedCustomFields: ISystemSettingsMappedCustomField[]) => ISystemSettingsMappedCustomField[]) {
		const newMappedCustomFields = cloneDeep(mappedCustomFields);

		const newMappedCustomFieldsChanged = actionFn(newMappedCustomFields);

		setMappedCustomFields(newMappedCustomFieldsChanged);
	}

	function handleOnAddNewInput(parentIndex: number) {
		doAnyChange((customFields: ISystemSettingsMappedCustomField[]) => {
			const newCustomField = { displayId: "", companyId: "", fieldId: "", fieldName: "" };
			customFields[parentIndex].payrollCustomFields.push(newCustomField);

			return customFields;
		});
	}

	function handleOnRemoveInput(parentIndex: number, childIndex: number) {
		doAnyChange((customFields: ISystemSettingsMappedCustomField[]) => {
			customFields[parentIndex].payrollCustomFields.splice(childIndex, 1);

			return customFields;
		});
	}

	function handleOnChangeInput(parentIndex: number, childIndex: number, value?: TSelectedValuesInput) {
		doAnyChange((customFields: ISystemSettingsMappedCustomField[]) => {
			const currentValue = customFields[parentIndex].payrollCustomFields[childIndex];

			if (!value) {
				currentValue.displayId = "";
				currentValue.companyId = "";
				currentValue.fieldId = "";
				currentValue.fieldName = "";
				return customFields;
			}

			const { value: val, extra } = value;

			if (!currentValue.displayId) {
				/* No displayId, setting displayId and companyId */
				currentValue.displayId = val;
				currentValue.companyId = extra;
			} else if (!currentValue.fieldName) {
				/* displayId set, setting fieldName and fieldId */
				currentValue.fieldName = val;
				currentValue.fieldId = extra;
			} else if (currentValue.fieldName) {
				/* Both set, resetting fieldName and fieldId */
				currentValue.fieldName = "";
				currentValue.fieldId = "";
			}

			customFields[parentIndex].payrollCustomFields[childIndex] = currentValue;

			return customFields;
		});
	}

	return (
		<>
			<Grid container direction={"row"} className="min-h-[40px] pb-6 mt-8">
				<Grid item className="position-mapping-rtc-column" xs={2.5}>
					<Typography className="position-mapping-table-header">Custom Field Item</Typography>
				</Grid>
				<Grid item className="position-mapping-payroll-column" xs={2.5}>
					<Typography className="position-mapping-table-header">Payroll Custom Field Item</Typography>
				</Grid>
			</Grid>
			{mappedCustomFields.map(({ _id, payrollCustomFields, systemCustomField }, index, array) => {
				const selectedDisplayIds = payrollCustomFields
					? payrollCustomFields.filter((input) => input.displayId && input.fieldName).map((input) => input.displayId)
					: [];

				const filteredOptions = customFieldsOptions.filter(({ displayId }) => !selectedDisplayIds.includes(displayId));

				const generalErrorInSiblingsRow = array.some((item, itemIndex) => {
					if (itemIndex === index) {
						return false;
					}
					return item.payrollCustomFields?.some((input) => !input.displayId || !input.fieldName);
				});

				return (
					<CustomFieldItemRow
						key={_id}
						customFieldItemIndex={index}
						name={systemCustomField}
						value={payrollCustomFields}
						options={filteredOptions}
						disabled={disabled || generalErrorInSiblingsRow}
						onAddNewInput={handleOnAddNewInput}
						onRemoveInput={handleOnRemoveInput}
						onChangeInput={handleOnChangeInput}
					/>
				);
			})}
		</>
	);
}
